import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Translation resources for public booking app
const resources = {
  en: {
    translation: {
      // Locale
      locale: "en-US",

      // Booking flow steps
      selectService: "Select Service",
      chooseYourService: "Choose Your Service",
      selectTeamMember: "Choose Team Member",
      selectTeamMemberImperativ: "Select Team Member",
      selectDateTime: "Pick Date & Time",
      selectDateTimeImperativ: "Select Date & Time",
      enterPersonalInfo: "Personal Information",
      confirmBooking: "Confirm Booking",
      bookNow: "Book Now",
      bookPayable: "Book with obligation to pay",
      bookingSuccess: "Booking Confirmed",

      // Form labels
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      emailAddress: "Email Address",
      phone: "Phone (optional)",
      phoneNumber: "Phone Number",
      notes: "Notes",
      yourInformation: "Your Information",
      enterYourInformation: "Enter Your Information",

      // Form placeholders
      enterYourFirstName: "Enter your first name",
      enterYourLastName: "Enter your last name",
      enterYourEmail: "Enter your email address",
      enterYourPhone: "Enter your phone number",

      // Permissions and consent
      contactPermissionText: "I would like to receive information about offers and news",
      privacyNoticeText: "You can revoke your consent at any time. Information about the processing of your data can be found in our privacy policy.",
      privacyPolicyUrl: "https://www.getsylva.de/datenschutz",

      // §356a / §357 right of withdrawal (Widerruf)
      withdrawalTitle: "Right of withdrawal",
      withdrawalAcknowledge: "I have read the cancellation policy (Widerrufsbelehrung) and understand that I can withdraw from this contract within 14 days.",
      withdrawalEarlyServiceConsent: "I expressly request that {{studio}} begins providing the service before the 14-day withdrawal period ends. I understand that my right of withdrawal expires once the service has been fully performed.",
      withdrawalShowDetails: "Show cancellation policy",
      withdrawalHideDetails: "Hide cancellation policy",
      withdrawalGermanBindingNote: "The legally binding cancellation policy is provided in German below.",
      withdrawalConsentRequired: "Please confirm the cancellation policy to continue.",

      // §356a withdrawal page (/widerruf)
      withdrawalPageTitle: "Withdraw from contract",
      withdrawalContractRef: "Contract reference",
      withdrawalIntro: "You can withdraw from this contract within 14 days without giving any reason. Click below to start your withdrawal.",
      withdrawalButton: "Withdraw from contract",
      withdrawalConfirmTitle: "Please confirm your withdrawal",
      withdrawalConfirmText: "By confirming, you withdraw from this contract. We will send you a confirmation of receipt by e-mail and refund any payments already received.",
      withdrawalConfirmButton: "Confirm withdrawal",
      withdrawalBack: "Back",
      withdrawalDoneTitle: "Withdrawal received",
      withdrawalDoneText: "Your withdrawal has been recorded. We have sent a confirmation of receipt to your e-mail address.",
      withdrawalErrorExpired: "The 14-day withdrawal period has expired. Please use \"Manage appointment\" to cancel under the salon's terms.",
      withdrawalErrorNotApplicable: "There is no statutory right of withdrawal for this contract.",
      withdrawalAlreadyDone: "This contract has already been withdrawn.",
      withdrawalGenericError: "We could not process your withdrawal. Please try again.",
      withdrawalRequestIntro: "Enter the e-mail address you used for your booking. We will send you a link to withdraw from your contract.",
      withdrawalRequestEmailLabel: "E-mail address",
      withdrawalRequestButton: "Send withdrawal link",
      withdrawalRequestSentTitle: "Check your e-mail",
      withdrawalRequestSentText: "If a booking exists for this e-mail address, we have sent you a withdrawal link.",

      // First visit question
      firstVisitQuestion: "Have you been here before?",
      firstVisitYes: "This will be my first time",
      firstVisitNo: "I have been here before",

      // Buttons and actions
      continue: "Continue",
      back: "Back",
      submit: "Submit",
      reschedule: "Reschedule",
      loading: "Loading...",
      book: "Book Appointment",
      cancel: "Cancel",
      editAppointment: "Edit Appointment",

      // Time formatting
      timeFormatHourMinute: "h:mm a",
      timeFormatFull: "h:mm:ss a",
      dateFormat: "M/d/yyyy",
      dateFormatLong: "EEEE, MMMM d, yyyy",
      dateFormatWeekdayShort: "EEE",
      dateFormatMonthYearShort: "MMM yyyy",
      dateFormatDayNumeric: "d",
      today: "Today",
      pageTitle: "Book Appointment with {{studioName}}",

      // Validation messages
      required: "This field is required",
      invalidEmail: "Please enter a valid email address",
      emailTypoSuggestion: "Did you mean",
      invalidPhone: "Please enter a valid phone number with country code",

      // Status messages
      available: "Available",
      unavailable: "Unavailable",
      booked: "Booked",

      // Error messages
      error: "Error",
      bookingFailed: "Failed to book appointment. Please try again.",
      noAvailableSlots: "No available time slots for this date.",
      sessionExpired: "Your session has expired. Please refresh the page.",
      importantBookingNotice: "Important Notice for Booking",
      bookingNotAvailable: "Booking Not Available",
      pleaseCheckUrl: "Please check the URL and try again.",

      // Loading states
      loadingBookingOptions: "Loading booking options...",

      // Validation errors
      firstNameRequiredError: "First name is required",
      lastNameRequiredError: "Last name is required",
      emailRequiredError: "Email address is required",
      phoneRequiredError: "Phone number is required",

      // Team member selection
      anyAvailableTeamMember: "Any Available Team Member",
      firstAvailableTeamMember: "First Available Team Member",
      multipleTeamMembersRequired: "Multiple Team Members Required",
      multipleTeamMembersExplanation: "Since these services are provided by different team members, we will automatically assign available team members for you.",
      noTeamMembersAvailable: "No Team Members Available",
      noTeamMembersForService: "No team members are available for the selected service(s).",

      // Date and time selection
      availableTimesTitle: "Available Times",
      availableTimesForDate: "Available Times for {{date}}",
      loadingAvailableTimes: "Loading available times...",
      noAvailableTimesDate: "No available times for this date",
      pleaseSelectDifferentDate: "Please select a different date",
      callUsCta: "No suitable time? Call now {{phone}}",
      slotsAvailable_one: "{{count}} slot available",
      slotsAvailable_other: "{{count}} slots available",

      // Booking confirmation
      confirmYourBooking: "Confirm Your Booking",
      appointmentDetails: "Appointment Details",
      bookingService: "Service",
      bookingTeamMember: "Team Member",
      bookingDate: "Date",
      bookingTime: "Time",
      bookingDuration: "Duration",
      price: "Price",
      minutes: "minutes",
      booking: "Booking",
      bookingError: "Failed to create booking. Please try again.",
      "booking.select": "Select",
      "booking.selected": "Selected",
      "booking.showMore": "Show more",
      "booking.showLess": "Show less",
      anyTeamMemberRequested: "Any available team member requested",
      errorFailedToCreateAppointment: "Failed to create appointment",

      // Success page
      bookingConfirmedTitle: "Booking Confirmed!",
      bookingSuccessMessage: "Thank you {{name}}! Your appointment has been confirmed.",
      yourAppointmentDetails: "Your Appointment Details",
      confirmationEmailSentMessage: "A confirmation email has been sent to you with all the details.",
      bookAnotherAppointment: "Book Another Appointment",
      backToWebsite: "Back to Website",

      // Service translations
      service: "Service",
      services: "Services",
      uncategorized: "Uncategorized",
      selectedServices: "Selected Services",
      total: "Total",
      totalPrice: "Total Price",
      visitBookingConfirmed: "Appointment booking confirmed!",
      visitBookingFailed: "Failed to create appointment booking",
      singleAppointment: "Single Appointment",
      visitBooking: "Appointment Booking",
      bookOneService: "Book one service",
      bookMultipleServices: "Book multiple services",

      // Visit Management
      manageAppointment: "Manage Your Appointment",
      appointmentNotFound: "Appointment not found",
      invalidOrExpiredAppointmentLink: "Invalid or expired appointment link",
      failedToLoadAppointment: "Failed to load appointment details",
      appointmentCancelled: "Appointment Cancelled",
      sameTimeSelected: "You've selected the same time as your current appointment. You can continue to keep the same time or select a different time.",
      appointmentCancelledSuccessfully: "Your appointment has been cancelled successfully",
      failedToCancelAppointment: "Failed to cancel appointment",
      rescheduleSuccessful: "Your appointment has been rescheduled successfully",
      appointmentManagementInfo: "You can reschedule or cancel your entire appointment. All services in the appointment will be updated together.",
      rescheduleAppointment: "Reschedule Appointment",
      rescheduleError: "Failed to reschedule appointment",
      cancelAppointment: "Cancel Appointment",
      appointmentHasBeenCancelled: "This appointment has been cancelled",
      bookNewAppointment: "Book New Appointment",
      confirmCancelTitle: "Cancel Appointment",
      confirmCancelMessage: "Are you sure you want to cancel this appointment? This action cannot be undone.",
      confirmCancel: "Yes, Cancel",
      keepAppointment: "Keep Appointment",
      shortNoticeCancelTitle: "Short-notice cancellation",
      shortNoticeCancelMessageWithFee: "This is a short-notice cancellation (less than {{hours}} hours before your appointment). Under {{studio}}'s terms, a cancellation fee of up to {{feePercent}}% may apply — that's about {{amount}}.",
      shortNoticeCancelMessageNoAmount: "This is a short-notice cancellation (less than {{hours}} hours before your appointment). Under {{studio}}'s terms, a cancellation fee of up to {{feePercent}}% of the service price may apply.",
      rescheduleInsteadFree: "Reschedule instead (free)",
      cancelAnyway: "Cancel anyway",
      cancellationPolicyBookingTitle: "Cancellation policy",
      cancellationPolicyBookingDefault: "For cancellations less than {{hours}} hours before the appointment, a fee of up to {{feePercent}}% of the service price may apply.",
      servicesInAppointment: "Services in Your Appointment",
      inThisAppointment: "included in this appointment",
      selectNewDateTime: "Select New Date & Time",
      rescheduleAppointmentInfo: "Choose a new date and time for your appointment. All services will be rescheduled to maintain the same sequence.",
      allServicesWillBeRescheduled: "All services will be rescheduled together",
      numberOfServices: "Number of Services",
      totalDuration: "Total Duration",
      appointmentSummary: "Appointment Summary",
      confirmRescheduleInfo: "Please review and confirm your new appointment date and time.",
      confirmReschedule: "Confirm Reschedule",
      currentDateTime: "Current Date & Time",
      newDateTime: "New Date & Time",

      // Status labels
      upcoming: "Upcoming",
      completed: "Completed",
      cancelled: "Cancelled",

      // Step titles
      confirmation: "Confirmation",
    }
  },
  de: {
    translation: {
      // Locale
      locale: "de-DE",

      // Booking flow steps
      yourInformation: "Ihre Informationen",
      enterYourInformation: "Geben Sie Ihre Informationen ein",
      selectService: "Service auswählen",
      chooseYourService: "Wählen Sie eine Dienstleistung",
      selectTeamMember: "Teammitglied wählen",
      selectTeamMemberImperativ: "Wählen Sie ein Teammitglied",
      selectDateTime: "Datum & Zeit wählen",
      selectDateTimeImperativ: "Wählen Sie Datum & Zeit",
      enterPersonalInfo: "Persönliche Daten",
      confirmBooking: "Bestätigen Sie Ihre Buchung",
      bookNow: "Jetzt buchen",
      bookPayable: "Zahlungspflichtig buchen",
      bookingSuccess: "Buchung bestätigt",

      // Form labels
      firstName: "Vorname",
      lastName: "Nachname",
      email: "E-Mail",
      emailAddress: "E-Mail-Adresse",
      phone: "Telefon (optional)",
      phoneNumber: "Telefonnummer",
      notes: "Notizen",

      // Form placeholders
      enterYourFirstName: "Geben Sie Ihren Vornamen ein",
      enterYourLastName: "Geben Sie Ihren Nachnamen ein",
      enterYourEmail: "Geben Sie Ihre E-Mail-Adresse ein",
      enterYourPhone: "Geben Sie Ihre Telefonnummer ein",

      // Permissions and consent
      contactPermissionText: "Ich möchte über Angebote und Neuigkeiten informiert werden",
      privacyNoticeText: "Es gilt unsere Datenschutzerklärung",
      privacyPolicyUrl: "https://www.getsylva.de/datenschutz",

      // §356a / §357 Widerrufsrecht
      withdrawalTitle: "Widerrufsrecht",
      withdrawalAcknowledge: "Ich habe die Widerrufsbelehrung zur Kenntnis genommen und weiß, dass ich diesen Vertrag binnen 14 Tagen widerrufen kann.",
      withdrawalEarlyServiceConsent: "Ich verlange ausdrücklich, dass {{studio}} bereits vor Ablauf der 14-tägigen Widerrufsfrist mit der Dienstleistung beginnt. Mir ist bekannt, dass mein Widerrufsrecht erlischt, wenn die Dienstleistung vollständig erbracht wurde.",
      withdrawalShowDetails: "Widerrufsbelehrung anzeigen",
      withdrawalHideDetails: "Widerrufsbelehrung ausblenden",
      withdrawalGermanBindingNote: "Die rechtlich maßgebliche Widerrufsbelehrung finden Sie nachfolgend auf Deutsch.",
      withdrawalConsentRequired: "Bitte bestätigen Sie die Widerrufsbelehrung, um fortzufahren.",

      // §356a Widerruf-Seite (/widerruf)
      withdrawalPageTitle: "Vertrag widerrufen",
      withdrawalContractRef: "Vertragskennung",
      withdrawalIntro: "Sie können diesen Vertrag binnen 14 Tagen ohne Angabe von Gründen widerrufen. Klicken Sie unten, um Ihren Widerruf zu starten.",
      withdrawalButton: "Vertrag widerrufen",
      withdrawalConfirmTitle: "Bitte bestätigen Sie Ihren Widerruf",
      withdrawalConfirmText: "Mit der Bestätigung widerrufen Sie diesen Vertrag. Sie erhalten eine Eingangsbestätigung per E-Mail, und bereits erhaltene Zahlungen werden Ihnen erstattet.",
      withdrawalConfirmButton: "Widerruf bestätigen",
      withdrawalBack: "Zurück",
      withdrawalDoneTitle: "Widerruf eingegangen",
      withdrawalDoneText: "Ihr Widerruf wurde erfasst. Wir haben eine Eingangsbestätigung an Ihre E-Mail-Adresse gesendet.",
      withdrawalErrorExpired: "Die 14-tägige Widerrufsfrist ist abgelaufen. Bitte nutzen Sie \"Termin verwalten\", um nach den Bedingungen des Salons zu stornieren.",
      withdrawalErrorNotApplicable: "Für diesen Vertrag besteht kein gesetzliches Widerrufsrecht.",
      withdrawalAlreadyDone: "Dieser Vertrag wurde bereits widerrufen.",
      withdrawalGenericError: "Ihr Widerruf konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.",
      withdrawalRequestIntro: "Geben Sie die E-Mail-Adresse ein, mit der Sie gebucht haben. Wir senden Ihnen einen Link, um Ihren Vertrag zu widerrufen.",
      withdrawalRequestEmailLabel: "E-Mail-Adresse",
      withdrawalRequestButton: "Widerrufs-Link senden",
      withdrawalRequestSentTitle: "Bitte prüfen Sie Ihre E-Mails",
      withdrawalRequestSentText: "Falls für diese E-Mail-Adresse eine Buchung besteht, haben wir Ihnen einen Widerrufs-Link gesendet.",

      // First visit question
      firstVisitQuestion: "Waren Sie schon einmal bei uns?",
      firstVisitYes: "Es wird das erste Mal",
      firstVisitNo: "Ich war bereits da",

      // Buttons and actions
      continue: "Weiter",
      back: "Zurück",
      submit: "Senden",
      reschedule: "Umbuchen",
      loading: "Wird geladen...",
      book: "Termin buchen",
      cancel: "Abbrechen",
      editAppointment: "Termin bearbeiten",

      // Time formatting
      timeFormatHourMinute: "HH:mm",
      timeFormatFull: "HH:mm:ss",
      dateFormat: "d.M.yyyy",
      dateFormatLong: "EEEE, d. MMMM yyyy",
      dateFormatWeekdayShort: "EEE",
      dateFormatMonthYearShort: "MMM yyyy",
      dateFormatDayNumeric: "d",
      today: "Heute",
      pageTitle: "Termin buchen mit {{studioName}}",

      // Validation messages
      required: "Dieses Feld ist erforderlich",
      invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
      emailTypoSuggestion: "Meinten Sie",
      invalidPhone: "Bitte geben Sie eine gültige Telefonnummer mit Vorwahl ein",

      // Status messages
      available: "Verfügbar",
      unavailable: "Nicht verfügbar",
      booked: "Gebucht",

      // Error messages
      error: "Fehler",
      bookingFailed: "Termin konnte nicht gebucht werden. Bitte versuchen Sie es erneut.",
      noAvailableSlots: "Keine verfügbaren Termine für dieses Datum.",
      sessionExpired: "Ihre Sitzung ist abgelaufen. Bitte aktualisieren Sie die Seite.",
      importantBookingNotice: "Wichtiger Hinweis zur Terminbuchung",
      bookingNotAvailable: "Buchung nicht verfügbar",
      pleaseCheckUrl: "Bitte überprüfen Sie die URL und versuchen Sie es erneut.",
      failedToLoadAppointment: "Fehler beim Laden der Termindetails",

      // Loading states
      loadingBookingOptions: "Buchungsoptionen werden geladen...",

      // Validation errors
      firstNameRequiredError: "Vorname ist erforderlich",
      lastNameRequiredError: "Nachname ist erforderlich",
      emailRequiredError: "E-Mail-Adresse ist erforderlich",
      phoneRequiredError: "Telefonnummer ist erforderlich",

      // Team member selection
      anyAvailableTeamMember: "Beliebiges verfügbares Teammitglied",
      firstAvailableTeamMember: "Erstes verfügbares Teammitglied",
      multipleTeamMembersRequired: "Mehrere Teammitglieder erforderlich",
      multipleTeamMembersExplanation: "Da diese Services von verschiedenen Teammitgliedern durchgeführt werden, wählen wir automatisch verfügbare Teammitglieder für Sie aus.",
      noTeamMembersAvailable: "Keine Teammitglieder verfügbar",
      noTeamMembersForService: "Für die ausgewählten Service(s) sind keine Teammitglieder verfügbar.",

      // Date and time selection
      availableTimesTitle: "Verfügbare Zeiten",
      availableTimesForDate: "Verfügbare Zeiten für {{date}}",
      loadingAvailableTimes: "Verfügbare Zeiten werden geladen...",
      noAvailableTimesDate: "Keine verfügbaren Zeiten für dieses Datum",
      pleaseSelectDifferentDate: "Bitte wählen Sie ein anderes Datum",
      callUsCta: "Kein passender Termin? Jetzt anrufen {{phone}}",
      slotsAvailable_one: "{{count}} Termin verfügbar",
      slotsAvailable_other: "{{count}} Termine verfügbar",

      // Booking confirmation
      confirmYourBooking: "Buchung bestätigen",
      appointmentDetails: "Termindetails",
      bookingService: "Service",
      bookingTeamMember: "Teammitglied",
      bookingDate: "Datum",
      bookingTime: "Zeit",
      bookingDuration: "Dauer",
      price: "Preis",
      minutes: "Minuten",
      booking: "Buchung",
      bookingError: "Buchung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
      "booking.select": "Auswählen",
      "booking.selected": "Ausgewählt",
      "booking.showMore": "Mehr anzeigen",
      "booking.showLess": "Weniger anzeigen",
      anyTeamMemberRequested: "Beliebiges verfügbares Teammitglied angefragt",
      errorFailedToCreateAppointment: "Termin konnte nicht erstellt werden",

      // Success page
      bookingConfirmedTitle: "Buchung bestätigt!",
      bookingSuccessMessage: "Vielen Dank {{name}}! Ihr Termin wurde bestätigt.",
      yourAppointmentDetails: "Ihre Termindetails",
      confirmationEmailSentMessage: "Eine Bestätigungsemail mit allen Details wurde an Sie gesendet.",
      bookAnotherAppointment: "Einen weiteren Termin buchen",
      backToWebsite: "Zurück zur Website",

      // Service translations
      service: "Dienstleistung",
      services: "Dienstleistungen",
      uncategorized: "Unkategorisiert",
      selectedServices: "Ausgewählte Dienstleistungen",
      total: "Gesamt",
      totalPrice: "Gesamtpreis",
      visitBookingConfirmed: "Terminbuchung bestätigt!",
      visitBookingFailed: "Terminbuchung konnte nicht erstellt werden",
      singleAppointment: "Einzeltermin",
      visitBooking: "Terminbuchung",
      bookOneService: "Ein Service buchen",
      bookMultipleServices: "Mehrere Services buchen",

      // Visit Management
      manageAppointment: "Termin Verwalten",
      manageVisit: "Ihren Termin verwalten",
      visitDetails: "Termindetails",
      visitNotFound: "Termin nicht gefunden",
      invalidOrExpiredVisitLink: "Ungültiger oder abgelaufener Terminlink",
      failedToLoadVisit: "Fehler beim Laden der Termindetails",
      visitCancelled: "Termin storniert",
      sameTimeSelected: "Sie haben die gleiche Zeit wie bei Ihrem aktuellen Termin ausgewählt. Sie können fortfahren, um die gleiche Zeit beizubehalten, oder eine andere Zeit wählen.",
      visitCancelledSuccessfully: "Ihr Termin wurde erfolgreich storniert",
      failedToCancelVisit: "Fehler beim Stornieren des Termins",
      rescheduleSuccessful: "Ihr Termin wurde erfolgreich umgebucht",
      visitRescheduledSuccessfully: "Ihr Termin wurde erfolgreich umgebucht",
      visitManagementInfo: "Sie können Ihren gesamten Termin umbuchen oder stornieren. Alle Services im Termin werden gemeinsam aktualisiert.",
      rescheduleVisit: "Termin umbuchen",
      rescheduleAppointment: "Termin umbuchen",
      rescheduleError: "Fehler beim Umbuchen des Termins",
      cancelVisit: "Termin stornieren",
      cancelAppointment: "Termin stornieren",
      visitHasBeenCancelled: "Dieser Termin wurde storniert",
      appointmentHasBeenCancelled: "Dieser Termin wurde storniert",
      bookNewAppointment: "Jetzt neuen Termin buchen",
      confirmCancelTitle: "Termin stornieren",
      confirmCancelMessage: "Sind Sie sicher, dass Sie diesen Termin stornieren möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
      confirmCancel: "Ja, stornieren",
      keepAppointment: "Termin behalten",
      shortNoticeCancelTitle: "Kurzfristige Absage",
      shortNoticeCancelMessageWithFee: "Das ist eine kurzfristige Absage (weniger als {{hours}} Stunden vor Ihrem Termin). Laut den Bedingungen von {{studio}} kann eine Ausfallgebühr von bis zu {{feePercent}} % anfallen – das entspricht etwa {{amount}}.",
      shortNoticeCancelMessageNoAmount: "Das ist eine kurzfristige Absage (weniger als {{hours}} Stunden vor Ihrem Termin). Laut den Bedingungen von {{studio}} kann eine Ausfallgebühr von bis zu {{feePercent}} % des Leistungspreises anfallen.",
      rescheduleInsteadFree: "Lieber verschieben (kostenlos)",
      cancelAnyway: "Trotzdem stornieren",
      cancellationPolicyBookingTitle: "Stornobedingungen",
      cancellationPolicyBookingDefault: "Bei Absagen weniger als {{hours}} Stunden vor dem Termin kann eine Gebühr von bis zu {{feePercent}} % des Leistungspreises anfallen.",
      servicesInVisit: "Services in Ihrem Termin",
      inThisVisit: "in diesem Termin enthalten",
      selectNewDateTime: "Neues Datum & neue Uhrzeit wählen",
      rescheduleVisitInfo: "Wählen Sie ein neues Datum und eine neue Uhrzeit für Ihren Termin.",
      allServicesWillBeRescheduled: "Alle Services werden gemeinsam umgebucht",
      numberOfServices: "Anzahl der Services",
      totalDuration: "Gesamtdauer",
      visitSummary: "Terminzusammenfassung",
      confirmRescheduleInfo: "Bitte überprüfen und bestätigen Sie Ihr neues Termindatum und die Uhrzeit.",
      confirmReschedule: "Umbuchung bestätigen",
      currentDateTime: "Aktuelles Datum & Uhrzeit",
      newDateTime: "Neues Datum & Uhrzeit",

      // Status labels
      upcoming: "Anstehend",
      completed: "Abgeschlossen",
      cancelled: "Storniert",

      // Step titles
      confirmation: "Buchung bestätigen",
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    interpolation: {
      escapeValue: false, // React already does escaping
    },
    detection: {
      // Optimized for public booking - don't cache language preference
      order: ['navigator', 'htmlTag', 'querystring', 'cookie', 'localStorage'],
      lookupQuerystring: 'lng',
      lookupCookie: 'i18next',
      lookupLocalStorage: 'i18nextLng',
      caches: [], // Don't persist language choice for public users
    }
  });

export default i18n;
